import { useRef, useEffect } from "react";
import {
  Brain,
  Minimize2,
  X,
  Trash2,
  Target,
  ShieldAlert,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChatMessages } from "./ChatMessages";
import { ChatInput } from "./ChatInput";
import { BrainErrorState, type BrainErrorType } from "./BrainErrorState";
import type { ChatMessage } from "@/hooks/useChatHistory";
import type { UserIntentProfileData } from "@/hooks/useUserIntentProfile";
import type { PageContext } from "@/hooks/usePageContext";

const QUICK_PROMPTS = [
  { label: "Que faire maintenant ?", query: "Selon mon profil et ma priorité actuelle, que puis-je faire maintenant ?" },
  { label: "Risques blessure", query: "Quels sont les risques de blessure liés à mon profil et mes objectifs ?" },
  { label: "Récupération", query: "Quelles recommandations de récupération s'appliquent à mon profil ?" },
  { label: "Mon objectif", query: "Quels faits scientifiques soutiennent mon objectif principal ?" },
];

interface Props {
  messages: ChatMessage[];
  loading: boolean;
  profile: UserIntentProfileData | null;
  pageContext: PageContext;
  onSend: (text: string) => void;
  onMinimize: () => void;
  onClose: () => void;
  onClear: () => void;
  onActionClick?: (actionKey: string) => void;
  brainError?: { type: BrainErrorType; waitMinutes?: number } | null;
  onRetry?: () => void;
}

export function ChatFullscreen({
  messages,
  loading,
  profile,
  pageContext,
  onSend,
  onMinimize,
  onClose,
  onClear,
  onActionClick,
  brainError,
  onRetry,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <div className="fixed inset-0 z-40 flex bg-background">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-72 border-r border-border bg-card p-4 space-y-4 overflow-y-auto">
        <div className="flex items-center gap-2">
          <div className="rounded-lg bg-primary/10 p-2">
            <Brain className="h-5 w-5 text-primary" />
          </div>
          <h2 className="font-display font-bold text-foreground text-sm">Cerveau Central</h2>
        </div>

        <div className="rounded-lg border border-border bg-secondary/50 p-3 space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Page actuelle</p>
          <Badge variant="outline" className="text-xs">{pageContext.page}</Badge>
        </div>

        {profile?.completed && (
          <div className="rounded-lg border border-border bg-secondary/50 p-3 space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Profil</p>
            <div className="space-y-1.5">
              <Badge variant="outline" className="text-xs border-primary/30 text-primary">
                <Target className="h-3 w-3 mr-1" />
                {profile.objectif_principal}
              </Badge>
              <Badge variant="outline" className="text-xs block w-fit">{profile.niveau_sportif}</Badge>
              <Badge variant="outline" className="text-xs block w-fit">{profile.priorite_actuelle}</Badge>
              {profile.blessures_connues?.some((b) => b.encore_active) && (
                <Badge variant="outline" className="text-xs border-yellow-500/30 text-yellow-500">
                  <ShieldAlert className="h-3 w-3 mr-1" />
                  Blessure active
                </Badge>
              )}
            </div>
          </div>
        )}

        {!profile?.completed && (
          <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <p className="text-xs text-yellow-400">Profil non configuré</p>
            </div>
          </div>
        )}

        <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            <span className="font-semibold text-primary">Zéro invention.</span>{" "}
            Raisonnement uniquement sur les faits validés.
          </p>
        </div>

        <div className="mt-auto pt-4">
          <Button variant="ghost" size="sm" className="text-xs text-muted-foreground w-full justify-start" onClick={onClear}>
            <Trash2 className="h-3 w-3 mr-2" />
            Effacer l'historique
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex items-center gap-3 px-4 py-3 border-b border-border bg-card/80 backdrop-blur-sm">
          <div className="rounded-lg bg-primary/10 p-2 lg:hidden">
            <Brain className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-display font-bold text-foreground lg:hidden">Cerveau Central</h1>
            <p className="text-xs text-muted-foreground truncate">
              {pageContext.page} — Raisonnement contextuel
            </p>
          </div>
          <Button size="sm" variant="ghost" className="text-xs lg:hidden" onClick={onClear}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" className="text-xs" onClick={onMinimize}>
            <Minimize2 className="h-3.5 w-3.5 mr-1.5" />
            Réduire
          </Button>
          <Button size="sm" variant="ghost" className="text-xs" onClick={onClose}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </header>

        {/* Messages */}
        {messages.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center space-y-3 py-12">
            <div className="rounded-xl bg-primary/10 p-4">
              <Brain className="h-8 w-8 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Pose une question basée sur les faits scientifiques validés et ton profil sportif.
            </p>
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {QUICK_PROMPTS.map((p) => (
                <Button
                  key={p.label}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  onClick={() => onSend(p.query)}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>
        ) : (
          <ChatMessages ref={scrollRef} messages={messages} loading={loading} onActionClick={onActionClick} />
        )}

        {/* Error state */}
        {brainError && (
          <div className="border-t border-border px-4 py-2">
            <div className="max-w-3xl mx-auto">
              <BrainErrorState
                errorType={brainError.type}
                waitMinutes={brainError.waitMinutes}
                onRetry={onRetry}
                inline
              />
            </div>
          </div>
        )}

        {/* Input */}
        <div className="border-t border-border bg-card/80 p-4">
          <div className="max-w-3xl mx-auto">
            <ChatInput loading={loading} onSend={onSend} />
          </div>
        </div>
      </div>
    </div>
  );
}
